/* 🔴 F15 STEP 3 — THE THREE QUESTIONS IN THE EXERCISE EDITOR (docs/F15-COACH-ENGINE-DESIGN.md §2.3, §5; 2026-10-30).
 *
 * (1) "Is this one of ours?" — answered SILENTLY when the row is linked (born linked, tier 1/2, or a decided link): one
 *     line names the neighbour. Tier 3 is asked ONCE as a line, `Like our X? Yes · No · It's like…`. A novel row is asked
 *     nothing; `It's like…` stays offered. (2) "What's different?" — the CHIPS of `engineDelta.CHIPS` (derived from the
 *     generated required list); a touched chip holds its value, an untouched one shows the neighbour's, inherited BY COPY.
 *     (3) READY — derived (`readyLine`), one line, beside the per-exercise switch as he said it 09-22 (a switch the coach
 *     turns ON; §6 row 3 is open). The switch can be ON only for a READY row: its home is the engine row, which exists only
 *     when every answer does (§3.2) — an unready row has nothing for the engine to seat, so there is nothing to turn on.
 *
 * The state lives in `useEngineAnswers` (declared by the editor ABOVE its early returns — codex `web-css`); this file only
 * draws it. Every write goes through `engineWrite`, which names the table's columns and not one more. */
import { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import { CATALOG_ROWS } from "../lib/catalogLink.generated";
import type { Link } from "../lib/catalogLink";
import {
  CHIPS, DRILL_KINDS, ENGINE_CATEGORIES, ENGINE_SELECT, EXERCISE_TYPES, GOAL_SLUGS, MOVEMENT_IDS, MUSCLE_FOLDERS,
  UNSAFE_JOINTS, engineSource, engineValues, engineWrite, isMobility, neighbourFromSource, oursQuestion, readyLine,
  stepDifficulty, unsafeFor, valuesFromRow,
  type ChipKey, type Delta, type EngineValues, type Neighbour,
} from "../lib/engineDelta";
import { vocabularyLabel } from "../lib/vocabulary";
import { equipmentName } from "../lib/equipmentLibrary";

type Opened = { source: string | null; engineUse: boolean; values: EngineValues | null };

export type EngineAnswers = ReturnType<typeof useEngineAnswers>;

/** The engine section's state. `name` is the draft's name (question 1 follows it); `held` the link the save will write. */
export function useEngineAnswers(ownId: string | null, name: string, held: Link | null, enabled: boolean) {
  const [opened, setOpened] = useState<Opened>({ source: null, engineUse: false, values: null });
  const [neighbour, setNeighbour] = useState<Neighbour | null>(null);
  const [delta, setDelta] = useState<Delta>({});
  const [engineUse, setEngineUse] = useState(false);
  /** Tier 3's answer this session: `null` asked, `no` the coach said no (the question goes; `It's like…` stays). */
  const [said, setSaid] = useState<"yes" | "no" | null>(null);
  const [chip, setChip] = useState<ChipKey | null>(null);
  const [liking, setLiking] = useState(false);
  const [own, setOwn] = useState<{ id: string; name: string; values: EngineValues }[]>([]);
  const [loaded, setLoaded] = useState(false);
  /** The coach named a neighbour this session ("Yes", or "It's like…") — an answer, so the window holds unsaved work. */
  const [chosen, setChosen] = useState(false);

  // The row's own engine answers, and the coach's READY rows (the "own" neighbours) — read once.
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase.from("coach_exercise_engine").select(`coach_exercise_id, source, engine_use, ${ENGINE_SELECT}`);
      const rows = (data ?? []) as unknown as (Record<string, unknown> & { coach_exercise_id: string; source: string; engine_use: boolean })[];
      const ids = rows.map((r) => r.coach_exercise_id).filter((id) => id !== ownId);
      const { data: names } = ids.length === 0 ? { data: [] }
        : await supabase.from("coach_exercises").select("id, name").in("id", ids);
      if (cancelled) return;
      const nameOf = new Map(((names ?? []) as { id: string; name: string }[]).map((n) => [n.id, n.name]));
      setOwn(rows.filter((r) => r.coach_exercise_id !== ownId && nameOf.has(r.coach_exercise_id))
        .map((r) => ({ id: r.coach_exercise_id, name: nameOf.get(r.coach_exercise_id)!, values: valuesFromRow(r) })));
      const mine = rows.find((r) => r.coach_exercise_id === ownId);
      if (mine !== undefined) {
        const o = { source: mine.source, engineUse: mine.engine_use, values: valuesFromRow(mine) };
        setOpened(o);
        setEngineUse(o.engineUse);
      }
      setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [ownId, enabled]);

  // Question 1, from the name and the link — or, for a row already answered, from the source it was answered from.
  const question = useMemo(() => oursQuestion(name, held, CATALOG_ROWS), [name, held]);
  const answeredFrom = neighbourFromSource(opened.source);
  const wantedId = neighbour !== null ? null
    : answeredFrom?.kind === "ours" ? answeredFrom.id
    : question.kind === "linked" || (question.kind === "ask" && said === "yes") ? question.id : null;

  // The neighbour's values are READ at the moment they are copied — the catalogue row as it stands now (§3.2).
  useEffect(() => {
    if (!enabled || !loaded || wantedId === null) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase.from("exercises").select(`id, name, ${ENGINE_SELECT}`).eq("id", wantedId).maybeSingle();
      if (cancelled || data === null) return;
      const r = data as unknown as Record<string, unknown> & { id: string; name: string };
      setNeighbour({ kind: "ours", id: r.id, name: r.name, values: valuesFromRow(r) });
    })();
    return () => { cancelled = true; };
  }, [wantedId, enabled, loaded]);

  // An opened row answered from one of the coach's own rows names it again.
  useEffect(() => {
    if (neighbour !== null || answeredFrom?.kind !== "own") return;
    const n = own.find((o) => o.id === answeredFrom.id);
    if (n !== undefined) setNeighbour({ kind: "own", id: n.id, name: n.name, values: n.values });
  }, [own, answeredFrom?.kind, answeredFrom?.id, neighbour]);

  /** The values as they stand: an opened engine row is the base (its answers are the coach's), else the neighbour's copy. */
  const base = opened.values ?? neighbour?.values ?? null;
  const values = engineValues(base, delta);
  const ready = readyLine(values);
  /* An ANSWER is a change; the silent copy of a linked row is not (opening a row must not read "Unsaved changes") — it is
     written by the row's next Save, the coach's confirm. */
  const dirty = chosen || said === "yes" || Object.keys(delta).length > 0 || engineUse !== opened.engineUse;

  function touch(next: Delta) { setDelta((d) => ({ ...d, ...next })); }
  function likeThis(n: Neighbour) {
    setNeighbour(n);
    setOpened((o) => ({ ...o, values: null }));
    setDelta({});
    setLiking(false);
    setChosen(true);
  }

  /** The upsert a save sends after the exercise row is written — null when there is nothing READY to write. */
  function write(coachExerciseId: string) {
    return engineWrite(coachExerciseId, values, opened.values !== null && Object.keys(delta).length === 0 && opened.source !== null
      ? opened.source : engineSource(neighbour, delta), engineUse);
  }
  /** After a save: what was written is the new opened state. */
  function saved() {
    const row = write("x");
    if (row !== null) setOpened({ source: row.source, engineUse, values });
    setDelta({});
    setChosen(false);
    setSaid(null);
  }

  return {
    question, said, setSaid, neighbour, delta, touch, values, ready, chip, setChip, liking, setLiking, likeThis, own,
    engineUse, setEngineUse, dirty, write, saved,
  };
}

/** The view. `kit` is the exercise's own equipment (the simple form's), offered as the Kit chip's one-press answer. */
export function EngineQuestions({ a, kit, muscles }: { a: EngineAnswers; kit: readonly string[]; muscles: readonly string[] }) {
  const v = a.values;
  const nb = a.neighbour;
  const [likeQuery, setLikeQuery] = useState("");
  const likeMatches = useMemo(() => {
    const q = likeQuery.trim().toLowerCase();
    if (q.length < 2) return [];
    const ours = CATALOG_ROWS.filter((r) => r.name.toLowerCase().includes(q)).slice(0, 6)
      .map((r) => ({ kind: "ours" as const, id: r.id, name: r.name }));
    const mine = a.own.filter((r) => r.name.toLowerCase().includes(q)).slice(0, 4)
      .map((r) => ({ kind: "own" as const, id: r.id, name: r.name }));
    return [...mine, ...ours];
  }, [likeQuery, a.own]);

  async function pick(m: { kind: "ours" | "own"; id: string; name: string }) {
    if (m.kind === "own") {
      const r = a.own.find((o) => o.id === m.id);
      if (r !== undefined) a.likeThis({ kind: "own", id: r.id, name: r.name, values: r.values });
    } else {
      const { data } = await supabase.from("exercises").select(`id, name, ${ENGINE_SELECT}`).eq("id", m.id).maybeSingle();
      if (data !== null) {
        const r = data as unknown as Record<string, unknown> & { id: string; name: string };
        a.likeThis({ kind: "ours", id: r.id, name: r.name, values: valuesFromRow(r) });
      }
    }
    setLikeQuery("");
  }

  const q = a.question;
  const askName = q.kind === "ask" ? CATALOG_ROWS.find((r) => r.id === q.id)?.name ?? null : null;
  const touched = (k: ChipKey) => CHIPS.find((c) => c.key === k)!.writes.some((c) => c in a.delta);
  const shown = (k: ChipKey): string => {
    switch (k) {
      case "hard": return v.difficulty_level === null ? "—" : `${v.difficulty_level}/10`;
      case "movement": return MOVEMENT_IDS.find((m) => m.id === v.movement_pattern_id)?.label ?? "—";
      case "kit": return v.equipment === null ? "—" : v.equipment.length === 0 ? "None" : v.equipment.map((e) => equipmentName(e, [])).join(", ");
      case "side": return v.is_unilateral === null ? "—" : v.is_unilateral ? "Yes" : "No";
      case "hold": return v.is_hold === null ? "—" : v.is_hold ? "Hold" : "Reps";
      case "for": return v.goals_served === null || v.goals_served.length === 0 ? "—"
        : v.goals_served.map((g) => GOAL_SLUGS.find((s) => s.slug === g)?.label ?? g).slice(0, 2).join(", ") + (v.goals_served.length > 2 ? "…" : "");
      case "kind": return v.category === null ? "—" : vocabularyLabel(v.category);
      case "unsafe": return v.injury_contraindications === null ? "—"
        : Object.keys(v.injury_contraindications).length === 0 ? "None"
        : Object.keys(v.injury_contraindications).map((j) => UNSAFE_JOINTS.find((u) => u.joint === j)?.label ?? j).join(", ");
      case "muscles": return v.primary_muscles === null || v.primary_muscles.length === 0 ? "—"
        : v.primary_muscles.map((m) => vocabularyLabel(m)).slice(0, 2).join(", ") + (v.primary_muscles.length > 2 ? "…" : "");
    }
  };
  const joints = Object.keys(v.injury_contraindications ?? {});
  const toggleIn = (list: readonly string[] | null, x: string) =>
    (list ?? []).includes(x) ? (list ?? []).filter((y) => y !== x) : [...(list ?? []), x];

  return (
    <section className="xe-row xe-engine" aria-labelledby="xe-engine">
      <div className="xe-engine-head">
        <h3 className="xe-label" id="xe-engine">Engine</h3>
        <label className="xe-switch" title={a.ready.ready ? undefined : "Answer what it needs first"}>
          <input type="checkbox" role="switch" className="review-mute-input" checked={a.engineUse && a.ready.ready}
                 disabled={!a.ready.ready} onChange={(e) => a.setEngineUse(e.target.checked)} />
          <span className="review-mute-track" aria-hidden="true" />
          <span>Use with the engine</span>
        </label>
      </div>
      <p className={"xe-ready" + (a.ready.ready ? " is-ready" : "")} role="status" data-ready={a.ready.ready ? "yes" : "no"}>
        {a.ready.ready ? "✓ " : ""}{a.ready.text}
      </p>

      {/* (1) IS THIS ONE OF OURS? */}
      <div className="xe-ours" data-question="ours">
        {nb !== null ? (
          <p className="xe-ours-line">
            <span>Like {nb.kind === "ours" ? "our" : "your"} <strong>{nb.name}</strong></span>
            <span aria-hidden="true">·</span>
            <button type="button" className="ours-text-action" onClick={() => a.setLiking(true)}>Change</button>
          </p>
        ) : a.question.kind === "ask" && a.said === null && askName !== null ? (
          <p className="xe-ours-line">
            <span>Like our <strong>{askName}</strong>?</span>
            <button type="button" className="ours-chip" onClick={() => a.setSaid("yes")}>Yes</button>
            <button type="button" className="ours-chip" onClick={() => a.setSaid("no")}>No</button>
            <button type="button" className="ours-text-action" onClick={() => a.setLiking(true)}>It&rsquo;s like…</button>
          </p>
        ) : !a.liking ? (
          <p className="xe-ours-line">
            <button type="button" className="ours-text-action" onClick={() => a.setLiking(true)}>It&rsquo;s like…</button>
          </p>
        ) : null}
        {a.liking && (
          <div className="xe-like">
            <input className="xb-input xe-like-input" type="search" autoFocus placeholder="Like which exercise?"
                   aria-label="Like which exercise?" value={likeQuery} onChange={(e) => setLikeQuery(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); a.setLiking(false); } }} />
            {likeMatches.length > 0 && (
              <div className="ours-chips xe-like-list" role="group" aria-label="Exercises">
                {likeMatches.map((m) => (
                  <button key={`${m.kind}:${m.id}`} type="button" className="ours-chip" onClick={() => { void pick(m); }}>
                    {m.name}{m.kind === "own" ? " · yours" : ""}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* (2) WHAT'S DIFFERENT? — a touched chip is filled; an untouched one reads the neighbour's value. */}
      <p className="xe-label xe-diff-label" id="xe-diff">What&rsquo;s different?</p>
      <div className="ours-chips xe-diff" role="group" aria-labelledby="xe-diff" data-question="different">
        {CHIPS.map((c) => (
          <button key={c.key} type="button" className={"ours-chip xe-diff-chip" + (touched(c.key) ? " is-touched" : "")}
                  aria-expanded={a.chip === c.key} aria-pressed={touched(c.key)}
                  onClick={() => a.setChip(a.chip === c.key ? null : c.key)}>
            <span className="xe-diff-name">{c.label}</span>
            <span className="xe-diff-value">{shown(c.key)}</span>
          </button>
        ))}
      </div>
      {a.chip !== null && (
        <div className="xe-diff-panel" role="group" aria-label={CHIPS.find((c) => c.key === a.chip)!.label}>
          {a.chip === "hard" && (nb?.values.difficulty_level != null ? (
            <div className="ours-chips">
              <button type="button" className="ours-chip" onClick={() => a.touch({ difficulty_level: stepDifficulty(v.difficulty_level, -1) })}>Easier</button>
              <button type="button" className="ours-chip" onClick={() => a.touch({ difficulty_level: stepDifficulty(v.difficulty_level, 1) })}>Harder</button>
              <span className="xe-hint">{nb.name} is {nb.values.difficulty_level}/10 · this {v.difficulty_level}/10</span>
            </div>
          ) : (
            <div className="ours-chips xe-difficulty">
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <button key={n} type="button" className="ours-chip" aria-pressed={v.difficulty_level === n}
                        onClick={() => a.touch({ difficulty_level: n })}>{n}</button>
              ))}
            </div>
          ))}
          {a.chip === "movement" && (
            <div className="ours-chips">
              {MOVEMENT_IDS.map((m) => (
                <button key={m.id} type="button" className="ours-chip" aria-pressed={v.movement_pattern_id === m.id}
                        onClick={() => a.touch({ movement_pattern_id: m.id, movement_pattern: m.legacy })}>{m.label}</button>
              ))}
            </div>
          )}
          {a.chip === "kit" && (
            <div className="ours-chips">
              <button type="button" className="ours-chip" aria-pressed={v.equipment !== null && v.equipment.length === 0}
                      onClick={() => a.touch({ equipment: [] })}>None</button>
              {kit.length > 0 && (
                <button type="button" className="ours-chip" onClick={() => a.touch({ equipment: [...kit] })}>
                  {kit.map((k) => equipmentName(k, [])).join(", ")}
                </button>
              )}
            </div>
          )}
          {a.chip === "side" && (
            <div className="ours-chips">
              <button type="button" className="ours-chip" aria-pressed={v.is_unilateral === false} onClick={() => a.touch({ is_unilateral: false })}>Both sides</button>
              <button type="button" className="ours-chip" aria-pressed={v.is_unilateral === true} onClick={() => a.touch({ is_unilateral: true })}>One side</button>
            </div>
          )}
          {a.chip === "hold" && (
            <div className="ours-chips">
              <button type="button" className="ours-chip" aria-pressed={v.is_hold === false} onClick={() => a.touch({ is_hold: false })}>Reps</button>
              <button type="button" className="ours-chip" aria-pressed={v.is_hold === true} onClick={() => a.touch({ is_hold: true })}>Hold</button>
            </div>
          )}
          {a.chip === "for" && (
            <div className="ours-chips">
              {GOAL_SLUGS.map((g) => (
                <button key={g.slug} type="button" className="ours-chip" aria-pressed={(v.goals_served ?? []).includes(g.slug)}
                        onClick={() => a.touch({ goals_served: toggleIn("goals_served" in a.delta ? v.goals_served : [], g.slug) })}>{g.label}</button>
              ))}
            </div>
          )}
          {a.chip === "kind" && (
            <>
              <div className="ours-chips">
                {ENGINE_CATEGORIES.map((c) => (
                  <button key={c} type="button" className="ours-chip" aria-pressed={v.category === c}
                          onClick={() => a.touch({ category: c, exercise_type: v.exercise_type, mobility_drill_kind: v.mobility_drill_kind })}>{vocabularyLabel(c)}</button>
                ))}
              </div>
              <div className="ours-chips xe-diff-sub">
                {EXERCISE_TYPES.map((t) => (
                  <button key={t} type="button" className="ours-chip" aria-pressed={v.exercise_type === t}
                          onClick={() => a.touch({ category: v.category, exercise_type: t, mobility_drill_kind: v.mobility_drill_kind })}>{vocabularyLabel(t)}</button>
                ))}
              </div>
              {isMobility(v.category) && (
                <div className="ours-chips xe-diff-sub">
                  {DRILL_KINDS.map((k) => (
                    <button key={k} type="button" className="ours-chip" aria-pressed={v.mobility_drill_kind === k}
                            onClick={() => a.touch({ category: v.category, exercise_type: v.exercise_type, mobility_drill_kind: k })}>{vocabularyLabel(k.replace("_", " "))}</button>
                  ))}
                </div>
              )}
            </>
          )}
          {a.chip === "unsafe" && (
            <div className="ours-chips">
              <button type="button" className="ours-chip" aria-pressed={v.injury_contraindications !== null && joints.length === 0}
                      onClick={() => a.touch({ injury_contraindications: {} })}>None</button>
              {UNSAFE_JOINTS.map((j) => (
                <button key={j.joint} type="button" className="ours-chip" aria-pressed={joints.includes(j.joint)}
                        onClick={() => a.touch({ injury_contraindications: unsafeFor(toggleIn(joints, j.joint)) })}>{j.label}</button>
              ))}
            </div>
          )}
          {a.chip === "muscles" && (
            <div className="xe-folders">
              {muscles.length > 0 && (
                <p className="xe-hint">
                  <button type="button" className="ours-text-action" onClick={() => a.touch({ primary_muscles: [...muscles] })}>
                    Use {muscles.map((m) => vocabularyLabel(m)).join(", ")}
                  </button>
                </p>
              )}
              {MUSCLE_FOLDERS.map((f) => (
                <details key={f.group} className="xe-folder">
                  <summary>{vocabularyLabel(f.group)}</summary>
                  <div className="ours-chips">
                    {f.muscles.map((m) => (
                      <button key={m.key} type="button" className="ours-chip" aria-pressed={(v.primary_muscles ?? []).includes(m.key)}
                              onClick={() => a.touch({ primary_muscles: toggleIn("primary_muscles" in a.delta ? v.primary_muscles : [], m.key) })}>{m.label}</button>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
